/*
 * NDT-tuloksen kirjaus hitsille (T525, Toni 11.10.2026 «T525 GO laajuudella «lomakkeet tarkastajalle»: Selailun punch- ja
 * NDT-lomake sekä punchin kuittaus tarkastajaroolille, vain demoon»).
 *
 * Yksi lomake kahdessa paikassa: hitsirivin «NDT +» -napin alle (tarkastaja ja työnjohto, sama roolijoukko kuin
 * POST /api/hitsit/:id/tarkastukset) ja Korjaa-lomakkeen NDT-osioon. Ennen tätä Korjaa-lomakkeen lisäys lähetti vain
 * menetelmän (tulos «kesken»), ja tulos asetettiin erillisellä valinnalla, joka poistettiin VT-erässä (ec9672b) — tulosta
 * ei siis voinut asettaa käyttöliittymästä kenellekään. Nyt tulos annetaan kirjattaessa, ja hylätty vaatii vikatyypin
 * («muu» → kuvaus pakollinen); palvelin tarkistaa saman ja kirjaa syyn huomioihin «RT hylätty: <vika> — <kuvaus>».
 * Hylätty pysyy Luovutusvalmiudessa avoimena samalla säännöllä kuin hylätty VT (T524).
 */
import { useState } from "react";
import { api, ApiError, type Hitsi } from "./api";
import { t, virheTeksti } from "./kieli";
import type { KaannosAvain } from "./kaannokset";
import { VT_VIAT } from "./VtKirjaus";
import { useKirjaustunniste } from "./kirjaustunniste";
import { PoytakirjaLiite } from "./PoytakirjaLiite";

export const NDT_VIAT = ["halkeama", "huokoisuus", "kuonasulkeuma", "liitosvirhe", "vajaa_lapihitsautuminen", "reunahaava", "muu"] as const;
const MENETELMAT = ["RT", "UT", "MT", "PT", "VT", "painekoe"] as const;
type Menetelma = typeof MENETELMAT[number];
const tanaan = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Helsinki" });
const viat = (m: Menetelma): readonly string[] => m === "VT" ? VT_VIAT : m === "painekoe" ? [] : NDT_VIAT;
const vikaNimi = (m: Menetelma, v: string) => t((m === "VT" ? `vt.vika.${v}` : `ndt.vika.${v}`) as KaannosAvain);

export function NdtLomake({ h, oletusTarkastaja, valmis, peru }: {
  h: Hitsi; oletusTarkastaja: string; valmis: () => void; peru?: () => void;
}) {
  const [menetelma, setMenetelma] = useState<Menetelma>("RT");
  const [tulos, setTulos] = useState<"hyvaksytty" | "hylatty" | "kesken">("hyvaksytty");
  const [paiva, setPaiva] = useState(tanaan());
  const [tarkastaja, setTarkastaja] = useState(oletusTarkastaja);
  const [raportti, setRaportti] = useState("");
  const [vika, setVika] = useState("");
  const [syy, setSyy] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);
  const [tunniste, vaihdaTunniste] = useKirjaustunniste();
  const vaatiiVian = tulos === "hylatty" && viat(menetelma).length > 0;
  const puuttuu = vaatiiVian && (!vika || (vika === "muu" && !syy.trim()));
  // T582 (Toni 15.10.2026): lopullinen tulos vaatii tarkastajan ja pöytäkirjan numeron (painekoe ja «kesken» eivät).
  // Toni 16.10.2026 klo 05.20: VT vaatii vain tarkastajan; pöytäkirjan numero RT/UT/MT/PT.
  const tunnistePuuttuu = menetelma !== "painekoe" && tulos !== "kesken" && (!tarkastaja.trim() || (menetelma !== "VT" && !raportti.trim()));
  async function tallenna() {
    setTallennetaan(true); setVirhe(null);
    try {
      await api.lisaaTarkastus(h.id, {
        kirjaustunniste: tunniste, menetelma, tulos, paiva,
        ...(tarkastaja.trim() && { tarkastaja: tarkastaja.trim() }),
        ...(raportti.trim() && { raportti: raportti.trim() }),
        ...(vaatiiVian && { vika, ...(syy.trim() && { syy: syy.trim() }) }),
      });
      vaihdaTunniste(); setRaportti(""); setVika(""); setSyy("");
      valmis();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("ndt.virhe")); }
    finally { setTallennetaan(false); }
  }
  return (
    <div className="ndt-lomake" data-ndt-lomake={h.id}>
      <strong>{t("ndt.otsikko", { nro: h.hitsinumero })}</strong>
      <div className="ndt-lomake__kentat">
        <label>{t("sel.menetelma")}
          <select value={menetelma} onChange={(e) => { setMenetelma(e.target.value as Menetelma); setVika(""); }} data-ndt-menetelma="">
            {MENETELMAT.map((m) => <option key={m} value={m}>{m === "painekoe" ? t("sel.painekoe") : m}</option>)}
          </select>
        </label>
        <label>{t("ndt.tulos")}
          <select value={tulos} onChange={(e) => setTulos(e.target.value as typeof tulos)} data-ndt-tulos-valinta="">
            <option value="hyvaksytty">{t("sel.ndt_hyvaksytty")}</option>
            <option value="hylatty">{t("sel.ndt_hylatty")}</option>
            <option value="kesken">{t("sel.ndt_kesken")}</option>
          </select>
        </label>
        <label>{t("hl.paiva")}<input type="date" value={paiva} onChange={(e) => setPaiva(e.target.value)} /></label>
        <label>{t("ltar.tarkastaja")}<input value={tarkastaja} maxLength={80} onChange={(e) => setTarkastaja(e.target.value)} /></label>
        <label>{t("iv.poytakirja")}<input value={raportti} maxLength={200} placeholder={t("sel.esim_poytakirja")}
                 onChange={(e) => setRaportti(e.target.value)} data-ndt-raportti="" /></label>
        <PoytakirjaLiite hitsiId={h.id} numero={raportti} />
        {vaatiiVian && <>
          <label>{t("vt.vika")}
            <select value={vika} onChange={(e) => setVika(e.target.value)} data-ndt-vika="">
              <option value="">{t("vt.valitse_vika")}</option>
              {viat(menetelma).map((v) => <option key={v} value={v}>{vikaNimi(menetelma, v)}</option>)}
            </select>
          </label>
          <label>{vika === "muu" ? t("vt.kuvaus_pakollinen") : t("vt.kuvaus")}
            <input value={syy} maxLength={300} onChange={(e) => setSyy(e.target.value)} data-ndt-syy="" /></label>
        </>}
      </div>
      <div className="ndt-lomake__napit">
        <button type="button" className="nappi nappi--paa" disabled={tallennetaan || puuttuu || tunnistePuuttuu} onClick={() => void tallenna()} data-ndt-tallenna="">
          {t("sel.lisaa_tarkastus")}</button>
        {peru && <button type="button" className="nappi nappi--kevyt" onClick={peru}>{t("hl.peruuta")}</button>}
        {puuttuu && <span className="dim" data-este-syy="">{!vika ? t("vt.valitse_vika") : t("vt.kuvaus_pakollinen")}</span>}
        {!puuttuu && tunnistePuuttuu && <span className="dim" data-este-syy="">{t("ndt.tunnistetiedot_pakolliset")}</span>}
      </div>
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
    </div>
  );
}

/** Hitsirivin alle avautuva NDT-kirjaus (sama lomake kuin Korjaa-lomakkeessa). */
export function NdtRivi({ h, sarakkeita, oletusTarkastaja, valmis, peru }: {
  h: Hitsi; sarakkeita: number; oletusTarkastaja: string; valmis: () => void; peru: () => void;
}) {
  return (
    <tr className="tbl__laajennus" data-ndt-rivi={h.id}>
      <td colSpan={sarakkeita}><NdtLomake h={h} oletusTarkastaja={oletusTarkastaja} valmis={valmis} peru={peru} /></td>
    </tr>
  );
}
